import React, { useState, useEffect } from 'react';
import './WaiterDashboard.css';
import workTicketService from '../../../services/api/workTicketService';

const WaiterDashboard = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [completingTickets, setCompletingTickets] = useState(new Set());

  const fetchTickets = async () => {
    try {
      setError(null);
      const response = await workTicketService.getPendingServeTickets();
      
      if (response.statusCode === 200 && response.data) {
        setTickets(response.data);
      } else {
        setTickets([]);
      }
    } catch (err) {
      setError('Failed to load tickets. Please try again.');
      console.error('Error fetching tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchTickets();
    }, 5000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const handleCompleteTicket = async (ticketId) => {
    setCompletingTickets(prev => new Set(prev).add(ticketId));
    
    try {
      const response = await workTicketService.completeTicket(ticketId);
      
      if (response.statusCode === 200) {
        setTickets(prev => prev.filter(ticket => ticket.id !== ticketId));
      } else {
        setError('Failed to complete ticket. Please try again.');
      }
    } catch (err) {
      setError('Failed to complete ticket. Please try again.');
      console.error('Error completing ticket:', err);
    } finally {
      setCompletingTickets(prev => {
        const newSet = new Set(prev);
        newSet.delete(ticketId);
        return newSet;
      });
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const created = new Date(timestamp);
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 min ago';
    if (diffMins < 60) return `${diffMins} mins ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return '1 hour ago';
    return `${diffHours} hours ago`;
  };

  const isUrgent = (timestamp) => {
    const now = new Date();
    const created = new Date(timestamp);
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    return diffMins > 5;
  };

  if (loading) {
    return (
      <div className="waiter-dashboard">
        <div className="loading-spinner">Loading tickets...</div>
      </div>
    );
  }

  return (
    <div className="waiter-dashboard">
      <div className="waiter-header">
        <h1>🍽️ Waiter Dashboard</h1>
        <p className="subtitle">Manage your serving tasks</p>
      </div>

      <div className="waiter-controls">
        <button 
          className={`waiter-control-btn ${autoRefresh ? 'active' : ''}`}
          onClick={() => setAutoRefresh(!autoRefresh)}
        >
          <span>{autoRefresh ? '🔄 Live' : '⏸️ Paused'}</span>
        </button>
        <button 
          className="waiter-control-btn"
          onClick={fetchTickets}
          disabled={loading}
        >
          <span>↻ Refresh</span>
        </button>
      </div>

      <div className="waiter-tickets-container">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="stats-bar">
          <div className="stat-item">
            <span className="stat-value">{tickets.length}</span>
            <span className="stat-label">Pending Serves</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">
              {tickets.filter(t => isUrgent(t.createdAt)).length}
            </span>
            <span className="stat-label">Urgent (5+ mins)</span>
          </div>
        </div>

        {tickets.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">✅</div>
            <h3>All caught up!</h3>
            <p>No pending serving tasks at the moment.</p>
          </div>
        ) : (
          <div className="tickets-grid">
            {tickets.map(ticket => {
              const isCompleting = completingTickets.has(ticket.id);
              const urgent = isUrgent(ticket.createdAt);
              
              return (
                <div key={ticket.id} className="serve-ticket-card">
                  <div className="serve-ticket-header">
                    <div>
                      <span className="serve-ticket-id">#{ticket.id}</span>
                      {urgent && <span className="urgent-badge">🔥 URGENT</span>}
                    </div>
                    <span className="serve-ticket-time">{getTimeAgo(ticket.createdAt)}</span>
                  </div>

                  <div className="serve-ticket-body">
                    <div className="serve-ticket-info-row">
                      <span className="serve-ticket-label">Order ID:</span>
                      <span className="serve-ticket-value">{ticket.orderId}</span>
                    </div>
                    <div className="serve-ticket-info-row">
                      <span className="serve-ticket-label">Item:</span>
                      <span className="serve-ticket-value">{ticket.itemName}</span>
                    </div>
                    <div className="serve-ticket-info-row">
                      <span className="serve-ticket-label">Quantity:</span>
                      <span className="serve-ticket-value">{ticket.quantity}</span>
                    </div>
                    <div className="serve-ticket-info-row">
                      <span className="serve-ticket-label">Assigned to:</span>
                      <span className="serve-ticket-value">{ticket.workerName}</span>
                    </div>
                    <div className="serve-ticket-info-row">
                      <span className="serve-ticket-label">Customer Ticket:</span>
                      <span className="serve-ticket-value">{ticket.customerTicketId}</span>
                    </div>
                    <div className="serve-ticket-info-row">
                      <span className="serve-ticket-label">Time:</span>
                      <span className="serve-ticket-value">{formatTime(ticket.createdAt)}</span>
                    </div>

                    {ticket.instructions && (
                      <div className="serve-ticket-instructions">
                        <span className="serve-ticket-instructions-label">📋 Instructions:</span>
                        <p className="serve-ticket-instructions-text">{ticket.instructions}</p>
                      </div>
                    )}
                  </div>

                  <div className="serve-ticket-footer">
                    <button 
                      className={`serve-complete-btn ${isCompleting ? 'completing' : ''}`}
                      onClick={() => handleCompleteTicket(ticket.id)}
                      disabled={isCompleting}
                    >
                      {isCompleting ? '⏳ Completing...' : '✅ Mark as Served'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WaiterDashboard;

